import { History, LoaderCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { parseServerTime } from "@/features/uploads/upload-metrics";
import {
  apiClient,
  type ClassificationEdit,
  type ClassificationRevertResult
} from "@/shared/api/api-client";
import { ApiState } from "@/shared/components/ApiState";
import { ConfirmDialogFrame } from "@/shared/components/ConfirmDialog";
import { ApiError, errorMessage } from "@/shared/api/http";
import {
  CLASSIFICATION_EDIT_KIND_LABELS,
  type ClassificationEditKind
} from "@/shared/vocabulary/classification-edit-kinds";
import { describeEdit, describeRevert } from "./edit-diff";

/** O teto da trilha por menção no servidor (`_HISTORY_LIMIT`): com ele
 *  cheio, há edição mais antiga que a lista não mostra. */
const HISTORY_LIMIT = 50;

/** A versão ORIGINAL no seletor — antes da primeira edição registrada. */
const ORIGINAL = "original";

type Choice = number | typeof ORIGINAL;

function when(iso: string): string {
  const parsed = parseServerTime(iso);
  return parsed === null ? iso : new Date(parsed).toLocaleString("pt-BR");
}

/** A versão atual (a primeira da lista) não se escolhe. */
function selectable(choice: Choice | null, list: ClassificationEdit[]): boolean {
  if (choice === null || list.length === 0) return false;
  return choice === ORIGINAL || (choice !== list[0].id && list.some(edit => edit.id === choice));
}

function kindLabel(kind: ClassificationEdit["kind"]): string {
  return CLASSIFICATION_EDIT_KIND_LABELS[(kind ?? "manual") as ClassificationEditKind] ?? String(kind);
}

/**
 * Voltar UMA menção a uma versão anterior (2026-10-31, "desfazer edição
 * individual"). Cada linha da trilha é uma versão — a menção como ficou
 * logo depois dela —, mais a ORIGINAL, antes da primeira edição. Escolher
 * uma versão risca as edições que vão ser desfeitas, pra quem confirma
 * ver o que perde.
 *
 * A conta é do servidor (`core/classification_edit/revert.py`): a tela
 * manda a versão e a edição mais nova que mostrava. Se alguém editou
 * nesse meio-tempo (409), a lista se refaz e a pessoa escolhe de novo.
 */
export function RevertVersionDialog({
  token,
  mentionId,
  mentionTitle,
  initialEditId,
  onClose,
  onReverted
}: {
  token: string;
  mentionId: number;
  mentionTitle: string;
  /** A linha de onde o diálogo foi aberto — vem marcada, se for versão. */
  initialEditId: number | null;
  onClose: () => void;
  onReverted: (result: ClassificationRevertResult) => void;
}) {
  const [edits, setEdits] = useState<ClassificationEdit[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [choice, setChoice] = useState<Choice | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const list = await apiClient.mentionClassificationHistory(token, mentionId);
      setEdits(list);
      // Recarregar (depois de um 409) mantém a escolha se ela ainda vale;
      // na primeira carga, a linha clicada vem marcada.
      setChoice(current =>
        selectable(current, list) ? current : selectable(initialEditId, list) ? initialEditId : null
      );
    } catch (failure) {
      setLoadError(errorMessage(failure, "Não foi possível carregar as versões da menção."));
    }
  }, [token, mentionId, initialEditId]);

  useEffect(() => {
    void load();
  }, [load]);

  const latestId = edits?.[0]?.id ?? null;
  // Quantas linhas, do topo, a escolha desfaz.
  const undoneCount =
    edits === null || choice === null
      ? 0
      : choice === ORIGINAL
        ? edits.length
        : edits.findIndex(edit => edit.id === choice);

  const confirm = async () => {
    if (choice === null || latestId === null || saving) return;
    setSaving(true);
    setError(null);
    try {
      const result = await apiClient.revertMentionClassification(
        token,
        mentionId,
        choice === ORIGINAL ? null : choice,
        latestId
      );
      onReverted(result);
    } catch (failure) {
      setError(errorMessage(failure, "Não foi possível voltar a menção pra essa versão."));
      if (failure instanceof ApiError && failure.status === 409) void load();
    } finally {
      setSaving(false);
    }
  };

  const truncated = edits !== null && edits.length >= HISTORY_LIMIT;

  return (
    <ConfirmDialogFrame
      title="Voltar a uma versão"
      tone="warning"
      icon={History}
      closeDisabled={saving}
      onClose={onClose}
      onSubmit={() => void confirm()}
      actions={
        <>
          <button type="button" className="secondary-button" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button type="submit" className="primary-button" disabled={saving || choice === null || undoneCount <= 0}>
            {saving ? <LoaderCircle className="spin" size={16} /> : "Voltar para esta versão"}
          </button>
        </>
      }
    >
      <p>
        <strong>{mentionTitle}</strong> volta a ficar como estava na versão escolhida
        {undoneCount > 0 ? ` — ${undoneCount === 1 ? "1 edição é desfeita" : `${undoneCount} edições são desfeitas`}` : ""}.
        A volta entra no histórico como uma edição nova, e dela também se volta.
      </p>

      {loadError && (
        <ApiState kind="error" onRetry={() => void load()}>
          <span role="alert">{loadError}</span>
        </ApiState>
      )}
      {edits === null && !loadError && <ApiState kind="loading">Carregando versões…</ApiState>}

      {edits !== null && (
        <fieldset className="revert-versions">
          <legend className="sr-only">Versões da menção</legend>
          {edits.map((edit, index) => {
            const current = index === 0;
            const undone = index < undoneCount;
            const revert = describeRevert(edit, when);
            const id = `revert-version-${edit.id}`;
            return (
              <label
                key={edit.id}
                htmlFor={id}
                className={`revert-version${current ? " is-current" : ""}${undone ? " is-undone" : ""}${choice === edit.id ? " is-chosen" : ""}`}
              >
                <input
                  id={id}
                  type="radio"
                  name="revert-version"
                  disabled={current || saving}
                  checked={choice === edit.id}
                  onChange={() => setChoice(edit.id)}
                />
                <span className="revert-version-body">
                  <span className="revert-version-head">
                    {when(edit.changed_at)} · {edit.changed_by_name ?? edit.changed_by} · {kindLabel(edit.kind)}
                    {current && <span className="report-criteria-chip">Versão atual</span>}
                    {undone && <span className="revert-version-tag">será desfeita</span>}
                  </span>
                  {revert && <span className="revert-version-line">{revert}</span>}
                  {describeEdit(edit).map(line => (
                    <span key={line.field} className="revert-version-line">
                      <em>{line.label}:</em> {line.text}
                    </span>
                  ))}
                </span>
              </label>
            );
          })}
          {edits.length > 0 && (
            <label
              htmlFor="revert-version-original"
              className={`revert-version${choice === ORIGINAL ? " is-chosen" : ""}`}
            >
              <input
                id="revert-version-original"
                type="radio"
                name="revert-version"
                disabled={saving}
                checked={choice === ORIGINAL}
                onChange={() => setChoice(ORIGINAL)}
              />
              <span className="revert-version-body">
                <span className="revert-version-head">Versão original</span>
                <span className="revert-version-line">
                  Como a menção estava antes da primeira edição registrada
                  {truncated ? ` (inclui edições mais antigas que as ${HISTORY_LIMIT} mostradas aqui)` : ""}.
                </span>
              </span>
            </label>
          )}
          {edits.length === 0 && <p>Esta menção não tem edição registrada — não há versão pra onde voltar.</p>}
        </fieldset>
      )}

      <p className="revert-versions-note">
        Volta a classificação (polaridade, abordagem, subtemas, destaque, mensagens-chave,
        porta-vozes e citação no título). Revisão, descarte e vínculo manual ficam como estão.
      </p>
      {error && (
        <div className="account-feedback error" role="alert">
          {error}
        </div>
      )}
    </ConfirmDialogFrame>
  );
}
